import { PixelCrop } from './types'

const createImage = (base64: string) =>
  new Promise((resolve, reject) => {
    const image = new Image()

    const onLoad = () => {
      image.removeEventListener('load', onLoad)
      image.removeEventListener('error', onError)
      resolve(image)
    }

    const onError = (error: Event | string) => {
      image.removeEventListener('load', onLoad)
      image.removeEventListener('error', onError)
      reject(error)
    }

    image.addEventListener('load', onLoad)
    image.addEventListener('error', onError)
    image.crossOrigin = 'anonymous'
    image.src = base64
  })

function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * @param {File} imageSrc - Image File
 * @param {Object} pixelCrop - pixelCrop Object provided by react-easy-crop
 * @param {number} rotation - optional rotation parameter
 *
 * Returns Promise
 * objectURL {string}
 * fileObj {File}
 */
export async function getCroppedImage(
  imageSrc: string,
  pixelCrop?: PixelCrop,
  rotation = 0,
): Promise<{ objectURL: string; fileObj: File }> {
  const image: any = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  const maxSize = Math.max(image.width, image.height)
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2))

  // set each dimensions to double largest dimension to allow for a safe area for the
  // image to rotate in without being clipped by canvas context
  canvas.width = safeArea
  canvas.height = safeArea

  // translate canvas context to a central location on image to allow rotating around the center.
  ctx?.translate(safeArea / 2, safeArea / 2)
  ctx?.rotate(getRadianAngle(rotation))
  ctx?.translate(-safeArea / 2, -safeArea / 2)

  // draw rotated image and store data.
  ctx?.drawImage(image, safeArea / 2 - image.width * 0.5, safeArea / 2 - image.height * 0.5)
  const data = ctx?.getImageData(0, 0, safeArea, safeArea)

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = Number(pixelCrop?.width)
  canvas.height = Number(pixelCrop?.height)

  // paste generated rotate image with correct offsets for x,y crop values.
  ctx?.putImageData(
    data as ImageData,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - Number(pixelCrop?.x)),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - Number(pixelCrop?.y)),
  )

  // As a blob
  return new Promise((resolve) => {
    canvas.toBlob((file) => {
      if (file) {
        resolve({
          objectURL: URL.createObjectURL(file),
          fileObj: new File([file as BlobPart], 'image-cropped.png', { type: 'image/png' }),
        })
      }
    }, 'image/png')
  })
}

export function convertToBase64(file: File) {
  const reader = new FileReader()
  reader.readAsDataURL(file)
  reader.onload = function () {
    return reader.result
  }
}
